/**
 * Tab Visibility Conditions for Configure Connector Dialog
 *
 * This file defines which tabs should be visible based on node category and other properties.
 * Easy to update without modifying the ConnectorConfigDialog component.
 *
 * Tab names:
 * - configuration: Basic connector settings (always visible)
 * - credentials: API credentials and authentication
 * - agent: AI Agent associations
 * - knowledgeBase: Knowledge base references
 * - channel: Channel configurations
 * - llmModel: LLM Model selections
 * - memory: Memory/State management
 * - agentFunction: Agent function assignments
 * - documentation: Node label, header, description (editable metadata)
 * - primaryConfig: Node type, usage, process element ID (read-only system info)
 */

export type TabName =
  | 'configuration'
  | 'credentials'
  | 'agent'
  | 'knowledgeBase'
  | 'channel'
  | 'llmModel'
  | 'memory'
  | 'agentFunction'
  | 'documentation'
  | 'primaryConfig'
  | 'identity';

export interface TabVisibilityRules {
  nodeCategory?: string;
  isTrigger?: boolean;
  isManual?: boolean;
  triggerType?: string; // 'webhook', 'schedule', 'manual', etc.
}

/**
 * Determine which tabs should be visible based on conditions
 * @param rules - Conditions to evaluate
 * @returns Array of visible tab names
 */
export const getVisibleTabs = (rules: TabVisibilityRules): TabName[] => {
  const visibleTabs: TabName[] = [];

  const {
    nodeCategory = '',
    isTrigger = false,
    isManual = false,
    triggerType = '',
  } = rules;

  // Configuration tab: ALWAYS show
  visibleTabs.push('configuration');

  // Identity tab: ALWAYS show — DID identity (signing/encryption keys) and trusted-DID allow-lists
  // are optional per-node capabilities any node type can opt into (gated by ProcessEngineOptions.
  // EnableNodeIdentity + the node's own enableSigning/enableEncryption flags at the backend), not
  // tied to nodeCategory the way Agent/KnowledgeBase/etc. are. The tab is always available; whether
  // it does anything meaningful is a backend/capability-config question, not a UI-visibility one.
  visibleTabs.push('identity');

 /*  // Special case: Show ALL tabs when trigger is manual
  if (isTrigger && isManual) {
    return [
      'configuration',
      'credentials',
      'agent',
      'knowledgeBase',
      'channel',
      'llmModel',
      'memory',
      'agentFunction',
    ];
  } */

  // Show all tabs for http nodes
  if (nodeCategory === 'http' || nodeCategory === 'action') {
    return [
      'configuration',
      'credentials',
      'identity'
    ];
  }

  // Add category-specific tabs based on nodeCategory
  switch (nodeCategory) {
    case 'ai-ml':       // Real DB category code for AI/ML nodes (ai-agent, openai-gpt, memory-store, etc.)
    case 'ai':          // kept for backward compatibility
    case 'ai-agent':    // kept for backward compatibility
      visibleTabs.push('agent');
      visibleTabs.push('llmModel');
      visibleTabs.push('knowledgeBase');
      visibleTabs.push('memory');
      visibleTabs.push('agentFunction');
      visibleTabs.push('credentials');
      break;

    case 'llm':         // kept for backward compatibility
      visibleTabs.push('llmModel');
      visibleTabs.push('agentFunction');
      visibleTabs.push('credentials');
      break;

    case 'ai-memory':   // kept for backward compatibility
      visibleTabs.push('memory');
      visibleTabs.push('agentFunction');
      visibleTabs.push('credentials');
      break;

    case 'tool':
    case 'utility':     // Function node (DB category code: 'utility', CategoryID 12)
      visibleTabs.push('agentFunction');
      visibleTabs.push('knowledgeBase');
      visibleTabs.push('credentials');
      break;

    case 'notification':
      // Notification nodes (Slack, email, Teams, SMS, etc.) only need credentials
      visibleTabs.push('credentials');
      break;

    case 'transform':
    case 'database':
    case 'integration':
    case 'error-handling':
    case 'file':
    case 'data-processing':
    case 'security':
    case 'analytics':
    case 'api':
      visibleTabs.push('credentials');
      break;

    case 'trigger':
      // For triggers, only show channel if it's webhook, otherwise only configuration
      if (triggerType === 'webhook') {
        visibleTabs.push('channel');
        visibleTabs.push('credentials');
      }
      // Other triggers (schedule, manual, etc.) don't get credentials
      break;

    case 'flow-control':   // if-condition, switch, loop, stop — actual DB category value
    case 'logic':          // legacy alias
    case 'conditions':     // legacy alias
    case 'human-in-the-loop': // FormNode, approval, review
    case 'data':           // variable-assignment, set-variable
    case 'variable':       // legacy alias
      // These nodes have dedicated config UIs; show documentation + info tabs only
      visibleTabs.push('documentation');
      visibleTabs.push('primaryConfig');
      break;

    default:
      // For any unknown/future category, show agentFunction and credentials
      if (nodeCategory) {
        visibleTabs.push('credentials');
      }
      break;
  }

  return visibleTabs;
};

/**
 * Check if a specific tab should be visible
 * @param tabName - Tab to check
 * @param rules - Conditions to evaluate
 * @returns true if tab should be visible
 */
export const isTabVisible = (tabName: TabName, rules: TabVisibilityRules): boolean => {
  const visibleTabs = getVisibleTabs(rules);
  return visibleTabs.includes(tabName);
};

/**
 * Get display properties for tabs (label, icon)
 */
export const getTabDisplayProperties = (
  tabName: TabName
): { label: string; icon: string } => {
  const properties: Record<TabName, { label: string; icon: string }> = {
    configuration: { label: 'Configuration', icon: '' },
    credentials: { label: 'Credentials', icon: '' },
    agent: { label: 'Agent', icon: '' },
    knowledgeBase: { label: 'Knowledge Base', icon: '' },
    channel: { label: 'Channel', icon: '' },
    llmModel: { label: 'LLM Model', icon: '' },
    memory: { label: 'Memory', icon: '' },
    agentFunction: { label: 'Agent Function', icon: '' },
    documentation: { label: 'Documentation', icon: '' },
    primaryConfig: { label: 'Info', icon: '' },
    identity: { label: 'Identity', icon: '' },
  };

  return properties[tabName];
};
